'use client';

import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons';
import { useEffect } from 'react';
import { useStore } from 'zustand';

import { CreateAccountForm } from './register-form.component';

import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.component';
import { useLoading } from '@/hooks/use-loading.hook';
import { cpfValidator } from '@/utils/validators';
import { setRegistrationStorage } from '@/storage/registration.storage';
import { setAuthTokens } from '@/storage/auth-tokens.storage';
import { StepKeys } from '@/app/(main)/_components/sign-in-modal/_types/header-auth-modal-step-keys.enum';
import { isPendingEmailValidation, login } from '@/services/auth/login.service';
import { formatISO8601toBRDate } from '@/utils/formatters/format-iso-8601-to-br-date';
import { resolveAuthenticatedUser } from '@/store/auth.store';
import { modalsOpeningStore, setOpenSignInModal } from '@/store/modal';
import { useFocus } from '@/hooks/use-focus.hook';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useToast } from '@/components/ui/use-toast';

const loginFormSchema = z.object({
  documentNumber: z.custom<string>(cpfValidator, {
    message: 'Informe seu CPF.',
  }),
  password: z.string().min(1, {
    message: 'Informe sua senha.',
  }),
});

type LoginForm = z.infer<typeof loginFormSchema>;

const defaultValues: Partial<LoginForm> = {
  documentNumber: '',
  password: '',
};

export default function LoginForm() {
  const loginCallback = useStore(modalsOpeningStore, (state) => state.loginCallback);
  const { handleJump } = useProgressiveModal<StepKeys>();
  const { startLoading, stopLoading, isLoading } = useLoading();
  const { htmlElRef, setFocus } = useFocus();
  const { toast } = useToast();

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginFormSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const handleForgotPassword = () => {
    handleJump({ key: StepKeys.FORGOT_PASSWORD });
  };

  const onSubmit = form.handleSubmit(async ({ documentNumber, password }) => {
    try {
      startLoading();

      const isPendingEmailValidationResponse = await isPendingEmailValidation(documentNumber, password);
      if (
        isPendingEmailValidationResponse
        && isPendingEmailValidationResponse.isPendingValidation
        && !isPendingEmailValidationResponse.isProfessional
      ) {
        const formData: CreateAccountForm = {
          documentNumber,
          password,
          email: isPendingEmailValidationResponse?.customer?.email,
          cellphone: isPendingEmailValidationResponse?.customer?.phone,
          legalName: isPendingEmailValidationResponse?.customer?.name,
          socialName: isPendingEmailValidationResponse?.customer?.socialName || '',
          birthdate: isPendingEmailValidationResponse?.customer?.birthday
            ? formatISO8601toBRDate(isPendingEmailValidationResponse.customer.birthday)
            : '',
          city: `${isPendingEmailValidationResponse?.customer?.location?.city} - ${isPendingEmailValidationResponse?.customer?.location?.state}`,
        };

        setRegistrationStorage({
          formData,
          maskedEmail: isPendingEmailValidationResponse.maskedEmail,
          message: '',
          hideSocialName: true,
          token: isPendingEmailValidationResponse.token,
        });

        handleJump({ key: StepKeys.PIN_CONFIRMATION });
        return;
      }
      const loginResponse = await login(documentNumber, password);

      if (!loginResponse.access_token) {
        toast.error('CPF ou senha incorretos. Tente novamente.');
        return;
      }

      const { access_token, refresh_token } = loginResponse;
      setAuthTokens({ access_token, refresh_token });

      setOpenSignInModal(false);
      await resolveAuthenticatedUser(access_token);
      loginCallback?.();
    } catch {
      toast.error('CPF ou senha incorretos. Tente novamente.');
    } finally {
      stopLoading();
    }
  });

  useEffect(() => {
    setFocus();
  }, []);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} aria-label="form">
        <FormField
          control={form.control}
          name="documentNumber"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel className="text-gray-900 font-semibold">CPF</FormLabel>
              <FormControl>
                <Input
                  placeholder="000.000.000-00"
                  mask="999.999.999-99"
                  {...field}
                  inputMode="numeric"
                  ref={htmlElRef}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel className="text-gray-900 font-semibold">Senha</FormLabel>
              <FormControl>
                <Input
                  startIcon={faLockKeyhole}
                  autoFocus
                  placeholder="Digite a sua senha"
                  type="password"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="button"
          color="primary"
          variant="text"
          className="mt-4 justify-start"
          onClick={handleForgotPassword}
        >
          Esqueci minha senha
        </Button>

        <Button
          isLoading={isLoading}
          type="submit"
          color="primary"
          variant="default"
          className="mt-6 w-full h-12"
        >
          Entrar
        </Button>
      </form>
    </Form>
  );
}
